/**
 * MergeContactsDialog - Dialog para unificar contatos manualmente
 * 
 * Permite ao administrador selecionar dois contatos para mesclar:
 * - Contato ORIGEM: será removido após merge
 * - Contato DESTINO: receberá conversas, deals, labels, tags do origem
 * 
 * Utiliza a RPC merge_contacts existente que:
 * - Valida tenant via get_validated_tenant_id
 * - Migra conversas, deals, labels, tags, campaigns, flow_executions
 * - Mescla channel_ids e metadata (precedência do target)
 * - Protege Instagram IGSID de perda
 * - Deleta o contato origem após migração
 * 
 * Versão: 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { getInitials } from '@/lib/utils/initials';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';
import { 
  Search, 
  ArrowDown, 
  AlertTriangle, 
  User, 
  Phone, 
  MessageSquare,
  Loader2,
  X
} from 'lucide-react';

// Logger para este módulo
const logger = createLogger('MergeContacts');

// ============================================================================
// INTERFACES
// ============================================================================

/** Contato simplificado para seleção no merge */
interface MergeContact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  conversation_count?: number;
}

/** Props do componente */
interface MergeContactsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  onSuccess: () => void;
}

/** Estatísticas retornadas pelo merge */
interface MergeStats {
  conversations: number;
  deals: number;
  labels: number;
  tags: number;
  campaigns: number;
  flow_executions: number;
}

// ============================================================================
// COMPONENTE - Card de seleção de contato
// ============================================================================

interface ContactSelectionCardProps {
  label: string;
  labelColor: 'destructive' | 'success';
  description: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchResults: MergeContact[];
  selectedContact: MergeContact | null;
  onSelectContact: (contact: MergeContact) => void;
  onClearContact: () => void;
  loading: boolean;
  excludeContactId?: string;
}

/**
 * Card de seleção de contato com busca e preview
 */
function ContactSelectionCard({
  label,
  labelColor,
  description,
  searchValue,
  onSearchChange,
  searchResults,
  selectedContact,
  onSelectContact,
  onClearContact,
  loading,
  excludeContactId,
}: ContactSelectionCardProps) {
  // Filtra resultados para não mostrar o contato já selecionado no outro campo
  const filteredResults = searchResults.filter(c => c.id !== excludeContactId);

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      {/* Header do card */}
      <div className="flex items-center gap-2">
        <Badge 
          variant={labelColor === 'destructive' ? 'destructive' : 'default'}
          className={cn(
            labelColor === 'success' && 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600'
          )}
        >
          {label}
        </Badge>
        <span className="text-sm text-muted-foreground">{description}</span>
      </div>

      {/* Campo de busca ou contato selecionado */}
      {selectedContact ? (
        <div className="flex items-center gap-3 p-3 rounded-md bg-muted/50 border">
          <Avatar className="h-10 w-10">
            <AvatarImage src={selectedContact.avatar_url || undefined} />
            <AvatarFallback className={cn(getAvatarColorClasses(selectedContact.name))}>
              {getInitials(selectedContact.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {selectedContact.name || 'Sem nome'}
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {selectedContact.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {selectedContact.phone}
                </span>
              )}
              {selectedContact.conversation_count !== undefined && (
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  {selectedContact.conversation_count} conversa(s)
                </span>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClearContact}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Input de busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou telefone..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Resultados da busca */}
          {loading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && searchValue.length >= 2 && filteredResults.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum contato encontrado
            </p>
          )}

          {!loading && filteredResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredResults.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => onSelectContact(contact)}
                  className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-accent transition-colors text-left"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={contact.avatar_url || undefined} />
                    <AvatarFallback className={cn(getAvatarColorClasses(contact.name), 'text-xs')}>
                      {getInitials(contact.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {contact.name || 'Sem nome'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {contact.phone || contact.email || 'Sem telefone'}
                    </p>
                  </div>
                  {contact.conversation_count !== undefined && contact.conversation_count > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {contact.conversation_count} conv.
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

/**
 * Dialog para unificar dois contatos manualmente
 */
export function MergeContactsDialog({
  open,
  onOpenChange,
  tenantId,
  onSuccess,
}: MergeContactsDialogProps) {
  // Estados de seleção
  const [sourceContact, setSourceContact] = useState<MergeContact | null>(null);
  const [targetContact, setTargetContact] = useState<MergeContact | null>(null);

  // Estados de busca
  const [searchSource, setSearchSource] = useState('');
  const [searchTarget, setSearchTarget] = useState('');
  const [sourceResults, setSourceResults] = useState<MergeContact[]>([]);
  const [targetResults, setTargetResults] = useState<MergeContact[]>([]);
  const [loadingSource, setLoadingSource] = useState(false);
  const [loadingTarget, setLoadingTarget] = useState(false);

  // Estado de merge
  const [merging, setMerging] = useState(false);

  // ============================================================================
  // BUSCA DE CONTATOS
  // ============================================================================

  /**
   * Busca contatos com contagem de conversas
   */
  const searchContacts = useCallback(async (
    query: string,
    setResults: (results: MergeContact[]) => void,
    setLoading: (loading: boolean) => void
  ) => {
    if (!query.trim() || query.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      // Busca contatos via RPC existente
      const { data: contacts, error } = await supabase.rpc('get_tenant_contacts_paginated', {
        p_tenant_id: tenantId,
        p_limit: 10,
        p_offset: 0,
        p_search: query,
        p_tag_id: null,
      });

      if (error) throw error;

      if (!contacts || contacts.length === 0) {
        setResults([]);
        return;
      }

      // Busca contagem de conversas para cada contato
      const contactIds = contacts.map((c: any) => c.id);
      const { data: conversationCounts, error: countError } = await supabase
        .from('conversations')
        .select('contact_id')
        .in('contact_id', contactIds);

      if (countError) {
        logger.warn('Erro ao buscar contagem de conversas', countError);
      }

      // Agrupa contagens por contact_id
      const countsMap: Record<string, number> = {};
      (conversationCounts || []).forEach((conv: any) => {
        countsMap[conv.contact_id] = (countsMap[conv.contact_id] || 0) + 1;
      });

      // Mapeia resultados
      const mappedContacts: MergeContact[] = contacts.map((c: any) => ({
        id: c.id,
        name: c.name,
        phone: c.phone,
        email: c.email,
        avatar_url: c.avatar_url,
        conversation_count: countsMap[c.id] || 0,
      }));

      setResults(mappedContacts);
    } catch (error) {
      logger.error('Erro na busca de contatos', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Debounce da busca de origem
  useEffect(() => {
    const timer = setTimeout(() => {
      searchContacts(searchSource, setSourceResults, setLoadingSource);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchSource, searchContacts]);

  // Debounce da busca de destino
  useEffect(() => {
    const timer = setTimeout(() => {
      searchContacts(searchTarget, setTargetResults, setLoadingTarget);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTarget, searchContacts]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Reseta o estado do dialog
   */
  const resetState = useCallback(() => {
    setSourceContact(null);
    setTargetContact(null);
    setSearchSource('');
    setSearchTarget('');
    setSourceResults([]);
    setTargetResults([]);
    setMerging(false);
  }, []);

  /**
   * Fecha o dialog e reseta estado
   */
  const handleClose = useCallback(() => {
    if (merging) return; // Não permite fechar durante merge
    resetState();
    onOpenChange(false);
  }, [merging, resetState, onOpenChange]);

  /**
   * Executa o merge dos contatos
   */
  const handleMerge = async () => {
    if (!sourceContact || !targetContact) {
      showToast.error('Selecione ambos os contatos');
      return;
    }

    if (sourceContact.id === targetContact.id) {
      showToast.error('Selecione dois contatos diferentes');
      return;
    }

    setMerging(true);
    try {
      logger.info('Iniciando merge', { source: sourceContact.id, target: targetContact.id });

      const { data, error } = await supabase.rpc('merge_contacts', {
        p_source_contact_id: sourceContact.id,
        p_target_contact_id: targetContact.id,
      });

      if (error) {
        // Tratamento de erros específicos
        if (error.message.includes('IGSID') || error.message.includes('Instagram')) {
          showToast.error(
            'Não é possível unificar',
            'O contato origem possui conversa ativa no Instagram que seria perdida'
          );
        } else if (error.message.includes('mesmo contato')) {
          showToast.error('Selecione dois contatos diferentes');
        } else {
          showToast.error('Erro ao unificar contatos', error.message);
        }
        logger.error('Erro no merge', error.message);
        return;
      }

      // Extrai estatísticas do resultado (cast para any para acessar merged_stats)
      const result = data as any;
      const stats: MergeStats = result?.merged_stats || {
        conversations: 0,
        deals: 0,
        labels: 0,
        tags: 0,
        campaigns: 0,
        flow_executions: 0,
      };

      // Monta mensagem de sucesso
      const migrated: string[] = [];
      if (stats.conversations > 0) migrated.push(`${stats.conversations} conversa(s)`);
      if (stats.deals > 0) migrated.push(`${stats.deals} negócio(s)`);
      if (stats.labels > 0) migrated.push(`${stats.labels} label(s)`);
      if (stats.tags > 0) migrated.push(`${stats.tags} tag(s)`);

      const description = migrated.length > 0
        ? `Migrados: ${migrated.join(', ')}`
        : 'Nenhum dado adicional migrado';

      showToast.success('Contatos unificados!', description);
      
      logger.info('Merge concluído com sucesso', stats);

      // Fecha dialog e atualiza lista
      resetState();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      logger.error('Erro inesperado no merge', error);
      showToast.error('Erro ao unificar contatos', 'Tente novamente');
    } finally {
      setMerging(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  const canMerge = sourceContact && targetContact && sourceContact.id !== targetContact.id;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Unificar Contatos
          </DialogTitle>
          <DialogDescription>
            Selecione dois contatos para unificar em um só. O contato de origem será excluído e todos os seus dados serão migrados para o contato de destino.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Card de seleção do contato ORIGEM */}
          <ContactSelectionCard
            label="ORIGEM"
            labelColor="destructive"
            description="será removido"
            searchValue={searchSource}
            onSearchChange={setSearchSource}
            searchResults={sourceResults}
            selectedContact={sourceContact}
            onSelectContact={setSourceContact}
            onClearContact={() => setSourceContact(null)}
            loading={loadingSource}
            excludeContactId={targetContact?.id}
          />

          {/* Seta indicando direção do merge */}
          <div className="flex justify-center">
            <div className="p-2 rounded-full bg-muted">
              <ArrowDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          {/* Card de seleção do contato DESTINO */}
          <ContactSelectionCard
            label="DESTINO"
            labelColor="success"
            description="receberá tudo"
            searchValue={searchTarget}
            onSearchChange={setSearchTarget}
            searchResults={targetResults}
            selectedContact={targetContact}
            onSelectContact={setTargetContact}
            onClearContact={() => setTargetContact(null)}
            loading={loadingTarget}
            excludeContactId={sourceContact?.id}
          />

          {/* Aviso sobre exclusão */}
          {sourceContact && targetContact && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                O contato <strong>"{sourceContact.name || 'Sem nome'}"</strong> será <strong>excluído permanentemente</strong> após a unificação.
              </p>
            </div>
          )}
        </div>

        <Separator />

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={merging}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleMerge}
            disabled={!canMerge || merging}
          >
            {merging ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Unificando...
              </>
            ) : (
              'Confirmar Unificação'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
